// Line Entry — the model on the client.
//
// `lineEntryOf` is the TypeScript twin of the engine's
// `metadata_loader.line_entry_of`: the same implicit-command and
// `lookup_add_lines` synthesis, so a command NAME the client sends is the name
// the engine resolves. Nothing here decides what a line contains — the engine
// builds every line (`fetch_lines` / `apply_lines`). The limit check below is
// display only (the chip on a grid line as the user types); the save re-checks
// against the live source.

import type {
  ColumnDef, LineCommand, LineCommandDocuments, LineCommandPicker, LineEntry, LineSource,
  SqlModel,
} from '../types'
import { evaluate, truthy, ExprError, DATE_FUNCS, SAFE_FUNCS } from '../../expr'

export const LOOKUP_SOURCE_PREFIX = 'lookup__'

export interface NormalizedLineEntry {
  view: 'grid' | 'popup'
  sources: LineSource[]
  sourcesByName: Record<string, LineSource>
  commands: Array<LineCommand & { documents: LineCommandDocuments; picker: LineCommandPicker; label: string }>
  toolbar: LineEntry['toolbar'] | null
  onHeaderChange: { column: string; do: 'run' | 'offer' | 'none'; commands: string[] } | null
  quickAdd: LineEntry['quick_add'] | null
}

const KINDS = new Set(['transaction', 'master', 'business_logic', 'process'])
const DOCS = new Set(['header', 'choose', 'header_or_choose', 'none'])
const PICKERS = new Set(['none', 'review', 'multi'])

function cols(sm: SqlModel): ColumnDef[] {
  return (sm.columns || []).map(w => w?.column).filter(Boolean) as ColumnDef[]
}

function truthyFlag(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  return ['1', 'true', 'yes', 'y'].includes(String(v ?? '').trim().toLowerCase())
}

function normCommand(
  c: LineCommand, src: LineSource,
): NormalizedLineEntry['commands'][number] {
  const docsCfg = src.documents || {}
  let documents = String(c.documents || '').trim().toLowerCase() as LineCommandDocuments
  if (!DOCS.has(documents)) {
    if (docsCfg.header_column) documents = docsCfg.choose ? 'header_or_choose' : 'header'
    else if (docsCfg.choose) documents = 'choose'
    else documents = 'none'
  }
  let picker = String(c.picker || '').trim().toLowerCase() as LineCommandPicker
  if (!PICKERS.has(picker)) {
    picker = String(src.picker?.mode || '').trim().toLowerCase() as LineCommandPicker
    if (!PICKERS.has(picker)) picker = src.from.kind === 'master' ? 'multi' : 'review'
  }
  return {
    ...c,
    documents,
    picker,
    label: String(c.label || src.label || c.name),
    fill: c.fill && typeof c.fill === 'object' ? c.fill : {},
  }
}

/** The normalised Line Entry configuration of a line form, or null. */
export function lineEntryOf(sm: SqlModel | null | undefined): NormalizedLineEntry | null {
  if (!sm) return null
  const raw: LineEntry = (sm.line_entry && typeof sm.line_entry === 'object') ? sm.line_entry : {}
  const sources: LineSource[] = []
  const seen = new Set<string>()
  for (const s of raw.sources || []) {
    if (!s || typeof s !== 'object') continue
    const name = String(s.name || '').trim()
    const kind = String(s.from?.kind || '').trim().toLowerCase()
    if (!name || seen.has(name) || !KINDS.has(kind)) continue
    seen.add(name)
    sources.push({ ...s, name, from: { ...s.from, kind: kind as LineSource['from']['kind'] } })
  }
  const synthCmds: LineCommand[] = []
  for (const col of cols(sm)) {
    if (!truthyFlag(col.lookup_add_lines)) continue
    if (!String(col.lookup || '').trim()) continue
    const cname = String(col.name || col.db_name || '').trim()
    if (!cname) continue
    const sname = `${LOOKUP_SOURCE_PREFIX}${cname.toLowerCase()}`
    if (seen.has(sname)) continue
    seen.add(sname)
    const label = String(col.heading || col.name || cname).trim() || cname
    sources.push({
      name: sname, label, synthetic: true,
      from: { kind: 'master', lookup_of: cname }, picker: { mode: 'multi' },
    })
    synthCmds.push({
      name: sname, label: `Add ${label}…`, source: sname, documents: 'none',
      picker: 'multi', synthetic: true,
    })
  }
  if (sources.length === 0) return null
  const byName: Record<string, LineSource> = {}
  for (const s of sources) byName[s.name] = s

  const commands: NormalizedLineEntry['commands'] = []
  if (Array.isArray(raw.commands) && raw.commands.length) {
    const cseen = new Set<string>()
    for (const c of raw.commands) {
      if (!c || typeof c !== 'object') continue
      const cname = String(c.name || '').trim()
      const sname = String(c.source || '').trim()
      if (!cname || cseen.has(cname) || !byName[sname]) continue
      cseen.add(cname)
      commands.push(normCommand(c, byName[sname]))
    }
  } else {
    for (const s of sources) {
      if (s.synthetic) continue
      commands.push(normCommand({ name: s.name, label: s.label || s.name, source: s.name }, s))
    }
  }
  const used = new Set(commands.map(c => c.source))
  for (const c of synthCmds) {
    if (!used.has(c.source)) commands.push(normCommand(c, byName[c.source]))
  }

  const view = raw.view === 'grid' ? 'grid' : 'popup'
  let onHeaderChange: NormalizedLineEntry['onHeaderChange'] = null
  if (raw.on_header_change && typeof raw.on_header_change === 'object') {
    const d = String(raw.on_header_change.do || 'offer').toLowerCase()
    onHeaderChange = {
      column: String(raw.on_header_change.column || '').trim(),
      do: (d === 'run' || d === 'none') ? d : 'offer',
      commands: (raw.on_header_change.commands || []).map(String).filter(Boolean),
    }
  }
  const quickAdd = raw.quick_add && byName[String(raw.quick_add.source || '')]
    ? raw.quick_add : null
  return {
    view, sources, sourcesByName: byName, commands,
    toolbar: raw.toolbar && typeof raw.toolbar === 'object' ? raw.toolbar : null,
    onHeaderChange, quickAdd,
  }
}

/** Every command the line form offers (declared, implicit and synthesised). */
export function lineCommandsOf(sm: SqlModel | null | undefined): NormalizedLineEntry['commands'] {
  return lineEntryOf(sm)?.commands || []
}

export type ToolbarEntry = NormalizedLineEntry['commands'][number] | 'sep'

export interface ToolbarLayout {
  /** Label on the split button's face. */
  buttonLabel: string
  /** The command the button face runs; null ⇒ the face only opens the menu. */
  primary: NormalizedLineEntry['commands'][number] | null
  /** Menu entries in order (a `'sep'` draws a divider). */
  menu: ToolbarEntry[]
}

/** The split button: `toolbar.primary` on its face, `toolbar.menu` in its
 *  drop-down (in the author's order, `-` for a divider). With no toolbar
 *  block, the first command is primary and the rest form the menu. */
export function toolbarLayout(
  le: NormalizedLineEntry, visible: (c: NormalizedLineEntry['commands'][number]) => boolean = () => true,
): ToolbarLayout {
  const cmds = le.commands.filter(visible)
  const byName = new Map(cmds.map(c => [c.name, c]))
  const tb = le.toolbar || {}
  let primary = tb.primary ? byName.get(tb.primary) || null : null
  const menu: ToolbarEntry[] = []
  if (Array.isArray(tb.menu) && tb.menu.length) {
    for (const ref of tb.menu) {
      if (ref === '-') { if (menu.length && menu[menu.length - 1] !== 'sep') menu.push('sep'); continue }
      const c = byName.get(ref)
      if (c && c !== primary) menu.push(c)
    }
    // Commands the author did not place still have to be reachable.
    const placed = new Set([primary?.name, ...tb.menu])
    const rest = cmds.filter(c => !placed.has(c.name))
    if (rest.length) {
      if (menu.length && menu[menu.length - 1] !== 'sep') menu.push('sep')
      menu.push(...rest)
    }
  } else {
    if (!primary) primary = cmds[0] || null
    menu.push(...cmds.filter(c => c !== primary))
  }
  while (menu.length && menu[menu.length - 1] === 'sep') menu.pop()
  return {
    buttonLabel: String(tb.button || primary?.label || (cmds.length ? 'Add lines' : '')),
    primary,
    menu,
  }
}

/** The line under a command's label in the menu: the author's
 *  `description`, else one built from what it does — its picker, its
 *  pre-filled values and where its documents come from. */
export function describeCommand(
  cmd: NormalizedLineEntry['commands'][number],
  labelOf: (col: string) => string = c => c,
): string {
  if (cmd.description) return cmd.description
  const fills = Object.entries(cmd.fill || {}).map(([col, v]) => {
    const t = String(v).trim().toLowerCase()
    const what = t === 'max' ? 'its limit' : t === 'min' ? 'its minimum'
      : (t === '0' || t === '') ? 'left to count' : String(v)
    return what === 'left to count' ? `${labelOf(col)} left to count` : `${labelOf(col)} = ${what}`
  })
  const first = cmd.documents === 'choose' ? 'Choose the documents, then '
    : ''
  if (cmd.picker === 'none') {
    const s = `${first}${first ? 'add' : 'Adds'} every line`
    return fills.length ? `${s}, ${fills.join(', ')}` : s
  }
  if (cmd.picker === 'multi') return `${first}${first ? 'tick' : 'Tick'} as many as you need — one line each`
  return `${first}${first ? 'tick' : 'Tick'} lines and change what differs`
}

const REF_RE = /\{(form|header)\.([A-Za-z_][A-Za-z0-9_]*)\}/g

function normKey(k: string): string {
  return String(k || '').trim().replace(/\s+/g, '_').toLowerCase()
}

/** `visible_when` over the header (presentation only — the engine refuses a
 *  command whose condition is false). An unparseable condition shows. */
export function commandVisible(
  cmd: { visible_when?: string }, header: Record<string, unknown>,
): boolean {
  const src = String(cmd.visible_when || '').trim()
  if (!src) return true
  const h: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(header || {})) h[normKey(k)] = v
  const variables: Record<string, unknown> = {}
  let n = 0
  const expr = src.replace(REF_RE, (_m, _scope, name: string) => {
    const v = `__le${n++}`
    variables[v] = h[normKey(name)]
    return v
  })
  try {
    return truthy(evaluate(expr, {
      variables, resolveName: (nm) => h[normKey(nm)],
      functions: { ...SAFE_FUNCS, ...DATE_FUNCS },
    }))
  } catch (e) {
    if (e instanceof ExprError) return true
    return true
  }
}

// ── display-only limit check ──────────────────────────────────────────────

export interface ResolvedLineLimit {
  family?: 'number' | 'date' | 'datetime' | 'text'
  min?: unknown
  max?: unknown
  tolerance?: number
  tolerance_pct?: number
  one_of?: unknown[]
  over?: 'warn' | 'block'
  status?: boolean
  status_labels?: string[]
}

function comparable(v: unknown, family: string | undefined): number | null {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return null
  if (family === 'date' || family === 'datetime') {
    const t = Date.parse(String(v))
    if (Number.isNaN(t)) return null
    return family === 'date' ? Math.floor(t / 86_400_000) : t / 60_000
  }
  const n = typeof v === 'number' ? v : Number(String(v).trim())
  return Number.isFinite(n) ? n : null
}

export interface LineLimitVerdict {
  errors: Record<string, string>
  warnings: Record<string, string>
  status: { column: string; index: number; label: string; delta?: number } | null
}

/** Re-evaluate a pulled line's resolved `_limits` against its CURRENT values,
 *  so the grid's chip and cell hints follow the user's edits. Mirrors the
 *  engine's `_check_limits`; the engine's verdict at save is the one that
 *  counts. Labels come from the limits themselves (engine-resolved). */
export function evaluateLineLimits(
  values: Record<string, unknown>,
  limits: Record<string, ResolvedLineLimit> | null | undefined,
  labelOf: (key: string) => string = k => k,
): LineLimitVerdict {
  const out: LineLimitVerdict = { errors: {}, warnings: {}, status: null }
  for (const [k, spec] of Object.entries(limits || {})) {
    const fam = spec.family
    const v = values[k]
    if (v == null || v === '') continue
    const label = labelOf(k)
    if (Array.isArray(spec.one_of) && spec.one_of.length) {
      const sv = String(v).trim().toLowerCase()
      const ok = spec.one_of.some(o => {
        const a = comparable(o, fam), b = comparable(v, fam)
        return a != null && b != null ? Math.abs(a - b) < 1e-9 : String(o).trim().toLowerCase() === sv
      })
      if (!ok) {
        const msg = `${label} must be one of ${spec.one_of.join(', ')}.`
        if (spec.over === 'warn') out.warnings[k] = msg
        else out.errors[k] = msg
        continue
      }
    }
    const cv = comparable(v, fam)
    if (cv == null) continue
    const lo = comparable(spec.min, fam)
    const hi = comparable(spec.max, fam)
    for (const [side, bound] of [['max', hi], ['min', lo]] as const) {
      if (bound == null) continue
      const eps = 1e-9 * Math.max(1, Math.abs(bound))
      const beyond = side === 'max' ? cv - bound : bound - cv
      if (beyond <= eps) continue
      let width: number | null = null
      if (spec.tolerance != null || spec.tolerance_pct != null) {
        width = 0
        if (spec.tolerance != null) width = Math.max(width, Math.abs(spec.tolerance))
        if (spec.tolerance_pct != null && (fam === 'number' || !fam)) {
          width = Math.max(width, Math.abs(bound) * Math.abs(spec.tolerance_pct) / 100)
        }
      }
      const word = side === 'max' ? 'exceeds the limit of' : 'is below the limit of'
      const msg = `${label} ${String(v)} ${word} ${String(spec[side])}`
      if (width != null && beyond > width + eps) out.errors[k] = `${msg} beyond the allowed tolerance.`
      else if (spec.over === 'warn') out.warnings[k] = `${msg}.`
      else out.errors[k] = `${msg}.`
      break
    }
    if (spec.status) {
      let idx = 1
      if (hi != null && (fam === 'number' || !fam)) {
        const eps = 1e-9 * Math.max(1, Math.abs(hi))
        idx = cv < hi - eps ? 0 : cv > hi + eps ? 2 : 1
      } else if (lo != null && cv < lo) idx = 0
      else if (hi != null && cv > hi) idx = 2
      const labels = spec.status_labels || ['', '', '']
      out.status = { column: k, index: idx, label: labels[idx] || '' }
      if (hi != null && (fam === 'number' || !fam) && idx !== 1) out.status.delta = cv - hi
    }
  }
  return out
}
